import { CaretDownOutlined, CheckOutlined, SearchOutlined } from '@vindr/icons';
import { ParamsGetOrderWorklist } from '@vindr/services/apis/order.api';
import { extendDateOptions } from '@vindr/ui/src/components/ui/DateRangePicker';
import { Button, Row, Select } from 'antd';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INITIAL_FILTER_ORDER_WORKLIST } from '.';
import { FILTER_TYPE } from './constants';
import useSearchOptions, { WorklistFilters } from './useSearchOptions';

const { Option } = Select;

// [0: Facility, 1: Scheduled Date, 2: Visit ID, 3: Patient Name, 4: Accession Number, 5: Status, 6: Patient ID, 7: Study Description]
// const INITIAL_SEARCH_FIELDS = [1, 3, 5, 2, 0];
const INITIAL_SEARCH_FIELDS = window?.config?.filters?.worklist || [
  WorklistFilters.RECEIVED_AT,
  WorklistFilters.PATIENT_ID,
  WorklistFilters.PATIENT_NAME,
  WorklistFilters.MODALITY,
  WorklistFilters.STATUS,
];

function Filter({ initFilters, onChange, forceSearch }) {
  const { t } = useTranslation('Vindoc');
  const [params, setParams] = useState<ParamsGetOrderWorklist>(initFilters);

  useEffect(() => {
    //? set default institution
    setParams(initFilters);
    onChange(initFilters);
  }, [JSON.stringify(initFilters)]);

  const handleOnChangeFilter = (
    p: ParamsGetOrderWorklist,
    type: keyof ParamsGetOrderWorklist,
    values,
    triggerSearch = false
  ) => {
    if (!type) return;
    let tempFilterData: ParamsGetOrderWorklist = {};
    if (type === FILTER_TYPE.ORDER_DATE) {
      tempFilterData = {
        ...p,
        order_date_from: values[0].toISOString(),
        order_date_to: values[1].toISOString(),
      };
    } else if (type === FILTER_TYPE.RECEIVED_AT) {
      tempFilterData = {
        ...p,
        patient_arrived_time_from: values[0].toISOString(),
        patient_arrived_time_to: values[1].toISOString(),
      };
    } else {
      tempFilterData = {
        ...p,
        [type]: values,
      };
    }
    setIsFiltered(true);
    if (triggerSearch) {
      onSearch(tempFilterData, type);
    } else {
      onChangeParams(tempFilterData);
    }
  };

  const SEARCH_OPTIONS = useSearchOptions(params, handleOnChangeFilter);

  const [searchFields, setSearchFields] = useState([...INITIAL_SEARCH_FIELDS]);
  const [isFiltered, setIsFiltered] = useState(false);

  const onChangeParams = useCallback((p) => {
    setParams(p);
  }, []);

  const onSearch = useCallback(
    (p, type) => {
      setParams(p);
      onChange(p, type);
    },
    [onChange]
  );

  // const handleOnSearch = () => {
  //   setIsFiltered(true);
  //   onSearch({ ...params });
  // };

  const handleChangeSearchKey = (key, replaceKey) => {
    const tempParams: ParamsGetOrderWorklist = { ...params };

    const deleteDateRangeKeys = (
      fromKey: keyof ParamsGetOrderWorklist,
      toKey: keyof ParamsGetOrderWorklist
    ) => {
      delete tempParams[fromKey];
      delete tempParams[toKey];
    };

    const addDateRangeKeys = (
      fromKey: keyof ParamsGetOrderWorklist,
      toKey: keyof ParamsGetOrderWorklist
    ) => {
      tempParams[fromKey] = extendDateOptions['Today'][0].toISOString();
      tempParams[toKey] = extendDateOptions['Today'][1].toISOString();
    };

    if (key === FILTER_TYPE.ORDER_DATE) {
      deleteDateRangeKeys('order_date_from', 'order_date_to');
    } else if (key === FILTER_TYPE.RECEIVED_AT) {
      deleteDateRangeKeys('patient_arrived_time_from', 'patient_arrived_time_to');
    } else {
      delete tempParams[key];
    }

    if (replaceKey === FILTER_TYPE.ORDER_DATE) {
      addDateRangeKeys('order_date_from', 'order_date_to');
    } else if (replaceKey === FILTER_TYPE.RECEIVED_AT) {
      addDateRangeKeys('patient_arrived_time_from', 'patient_arrived_time_to');
    }

    setIsFiltered(true);
    onChangeParams(tempParams);

    let tempSearchField = [...searchFields];
    const currentItemIndex = SEARCH_OPTIONS.findIndex((it) => it.key == key);
    const replaceItemIndex = SEARCH_OPTIONS.findIndex((it) => it.key == replaceKey);

    tempSearchField = tempSearchField.map((it) => (it == currentItemIndex ? replaceItemIndex : it));
    setSearchFields(tempSearchField);
  };

  const handleClearAllFilters = useCallback(() => {
    setSearchFields(INITIAL_SEARCH_FIELDS);
    setParams({ ...initFilters });
    onChange({ ...INITIAL_FILTER_ORDER_WORKLIST });
    setIsFiltered(false);
  }, [initFilters]);

  return (
    <Row className="ml-[-7px] space-y-2 pt-3 pb-6" align="bottom">
      <div>
        <Row className="space-y-2" align="bottom">
          {searchFields &&
            SEARCH_OPTIONS &&
            searchFields.map((idx) => {
              const field = SEARCH_OPTIONS[idx];
              const options = SEARCH_OPTIONS.filter(
                (_, i) => i == idx || !searchFields.includes(i)
              );
              const Component = field?.component;

              return (
                <div key={field?.key} className="search-item">
                  <Row>
                    <div>
                      <Select
                        size="small"
                        className="select-dropdown-light border-0"
                        popupClassName="min-w-40"
                        menuItemSelectedIcon={<CheckOutlined />}
                        suffixIcon={<CaretDownOutlined />}
                        value={field.key}
                        onChange={(value) => handleChangeSearchKey(field.key, value)}
                      >
                        {options.map((item, idx) => (
                          <Option key={idx} value={item.key}>
                            {t(item.text)}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </Row>
                  <Row className="pl-[7px]">
                    <div>{Component ? Component : null}</div>
                  </Row>
                </div>
              );
            })}
        </Row>
      </div>
      <div className="ml-[7px] mr-1">
        <Row>
          <div className="mr-[7px]">
            <Button
              type="primary"
              className="flex items-center"
              size="small"
              // type="primary"
              icon={<SearchOutlined />}
              onClick={() => forceSearch?.(params)}
            >
              {t('Search')}
            </Button>
          </div>
          <div className="mr-0">
            <Button
              className="filter-btn"
              size="small"
              disabled={!isFiltered}
              // icon={<ReloadOutlined />}
              onClick={() => handleClearAllFilters()}
            >
              {t('Clear Filter')}
            </Button>
          </div>
        </Row>
      </div>
    </Row>
  );
}

export default memo(Filter, (prev, next) => isEqual(prev.initFilters, next.initFilters));
